import { createProductPaymentSessionFn } from "@/actions/payment/handler";
import { listPublicProductsOptions } from "@/actions/product/handler";
import { ProductType, getProductTypeName } from "@/api/db/schema/product";
import { authClient } from "@/auth/auth-client";
import { IyzicoCheckoutDialog } from "@/components/payment/IyzicoCheckoutDialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  ArrowRight,
  Dumbbell,
  Filter,
  Loader2,
  Package,
  ShieldCheck,
  Shirt,
  ShoppingBag,
  Sparkles,
  Watch,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { z } from "zod";

type SortOption = "featured" | "price-asc" | "price-desc" | "newest";

const storeSearchSchema = z.object({
  payment: z.enum(["success", "failed"]).optional(),
  error: z.string().optional(),
});

export const Route = createFileRoute("/_app/store/")({
  validateSearch: storeSearchSchema,
  loader: async ({ context }) =>
    await context.queryClient.ensureQueryData(listPublicProductsOptions()),
  head: ({ loaderData }) => {
    const products = loaderData?.body?.all || [];
    const featuredProducts = products.filter((p: any) => p.isFeatured);
    const productCount = products.length;

    const featuredNames = featuredProducts
      .slice(0, 3)
      .map((p) => p.name)
      .join(", ");

    const title = "Mağaza - Spor Ürünleri | theCoach";
    const description =
      featuredProducts.length > 0
        ? `theCoach mağazasında ${productCount} spor ürünü: ${featuredNames}${featuredProducts.length > 3 ? " ve daha fazlası" : ""}. İyzico güvencesiyle 3D Secure güvenli alışveriş.`
        : `theCoach mağazasında ${productCount} spor ürünü. T-shirtler, ağırlıklar, ekipmanlar ve aksesuarlar. İyzico güvencesiyle 3D Secure güvenli alışveriş.`;

    const ogImage = featuredProducts[0]?.imageUrl || "";

    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:image", content: ogImage },
        { property: "og:type", content: "website" },
        {
          name: "keywords",
          content:
            "spor ürünleri, fitness ekipmanları, t-shirt, ağırlık, dumbbell, theCoach, spor mağazası, gym giyim, powerlifting, antrenman ekipmanları",
        },
        { name: "robots", content: "index, follow" },
      ],
    };
  },
  component: StorePageComponent,
});

type ProductItem = {
  productId: string;
  name: string;
  description: string;
  price: number;
  oldPrice?: number;
  imageUrl?: string;
  sizes?: string[];
  colors?: string[];
  isFeatured?: boolean;
  productType: ProductType;
  displayOrder: number;
};

// Product Card Component
function ProductCard({
  product,
  onPurchase,
  isPurchasing,
}: {
  product: ProductItem;
  onPurchase: (productId: string, name: string) => void;
  isPurchasing: boolean;
}) {
  return (
    <Card
      className={cn(
        "group relative flex flex-col overflow-hidden border-2 shadow-lg transition-all duration-300 hover:scale-[1.02] hover:shadow-xl",
        product.isFeatured
          ? "border-primary/20 bg-secondary/5"
          : "border-border hover:border-primary/30",
      )}
    >
      {/* Product Image */}
      <div className="relative h-48 w-full overflow-hidden bg-muted/30">
        {product.imageUrl ? (
          <img
            src={product.imageUrl}
            alt={product.name}
            className="h-full w-full object-contain transition-transform duration-300 group-hover:scale-105"
            loading="lazy"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-muted">
            <Package className="h-16 w-16 text-muted-foreground" />
          </div>
        )}
        {/* Featured badge */}
        {product.isFeatured && (
          <div className="absolute top-4 right-4 z-10">
            <span className="flex items-center gap-1 rounded-full bg-primary px-3 py-1 font-semibold text-primary-foreground text-xs shadow-lg">
              <Sparkles className="h-3 w-3" />
              ÖNE ÇIKAN
            </span>
          </div>
        )}
      </div>

      <CardHeader className="pt-4 pb-2">
        <div className="space-y-1">
          <CardTitle className="line-clamp-2 font-bold text-lg leading-tight">
            {product.name}
          </CardTitle>
          <CardDescription className="line-clamp-2 text-sm">
            {product.description}
          </CardDescription>
        </div>

        {/* Sizes */}
        {product.sizes && product.sizes.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {product.sizes.map((size) => (
              <span
                key={size}
                className="rounded-md bg-muted px-2 py-0.5 text-muted-foreground text-xs"
              >
                {size}
              </span>
            ))}
          </div>
        )}
      </CardHeader>

      <CardContent className="grow pb-4">
        {/* Price section */}
        <div className="flex items-baseline gap-2">
          {product.oldPrice && product.oldPrice !== product.price && (
            <span className="font-medium text-muted-foreground text-sm line-through">
              ₺{product.oldPrice.toLocaleString()}
            </span>
          )}
          <span className="font-extrabold text-2xl text-primary tracking-tight">
            ₺{product.price.toLocaleString()}
          </span>
        </div>
        {product.oldPrice && product.oldPrice !== product.price && (
          <div className="mt-1 inline-flex items-center rounded-full bg-destructive/10 px-2 py-0.5">
            <span className="font-medium text-destructive text-xs">
              %
              {Math.round(
                ((product.oldPrice - product.price) / product.oldPrice) * 100,
              )}{" "}
              İNDİRİM
            </span>
          </div>
        )}
      </CardContent>

      <CardFooter className="border-t bg-accent/20 p-4">
        <Button
          size="lg"
          disabled={isPurchasing}
          onClick={() => onPurchase(product.productId, product.name)}
          className="w-full bg-primary font-semibold text-primary-foreground shadow-md transition-all duration-200 hover:bg-primary/90 hover:shadow-lg active:scale-[0.98]"
        >
          {isPurchasing ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Hazırlanıyor...
            </>
          ) : (
            <>
              <ShoppingBag className="mr-2 h-4 w-4" />
              Hemen Satın Al
              <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
            </>
          )}
        </Button>
      </CardFooter>
    </Card>
  );
}

// Product Grid Component
function ProductGrid({
  products,
  onPurchase,
  purchasingId,
}: {
  products: ProductItem[];
  onPurchase: (productId: string, name: string) => void;
  purchasingId: string | null;
}) {
  if (products.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-lg border-2 border-muted-foreground/30 border-dashed py-16">
        <Package className="mb-4 h-12 w-12 text-muted-foreground" />
        <h3 className="font-medium text-lg text-muted-foreground">
          Bu kategoride ürün bulunamadı
        </h3>
        <p className="mt-1 text-muted-foreground text-sm">
          Yakında yeni ürünler eklenecektir
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {products.map((product) => (
        <ProductCard
          key={product.productId}
          product={product}
          onPurchase={onPurchase}
          isPurchasing={purchasingId === product.productId}
        />
      ))}
    </div>
  );
}

// Loading Skeleton for products
export function ProductSkeleton() {
  return (
    <Card className="flex flex-col overflow-hidden">
      <Skeleton className="h-48 w-full" />
      <CardHeader className="space-y-2 pt-4 pb-2">
        <Skeleton className="h-5 w-3/4" />
        <Skeleton className="h-4 w-full" />
      </CardHeader>
      <CardContent className="grow pb-4">
        <Skeleton className="h-8 w-24" />
      </CardContent>
      <CardFooter className="border-t bg-accent/20 p-4">
        <Skeleton className="h-10 w-full" />
      </CardFooter>
    </Card>
  );
}

// Skeleton Grid for loading state
export function ProductSkeletonGrid({ count = 8 }: { count?: number }) {
  return (
    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {Array.from({ length: count }).map((_, i) => (
        <ProductSkeleton key={i} />
      ))}
    </div>
  );
}

// Category filter buttons
const categories = [
  { value: "all", label: "Tümü", icon: Package },
  {
    value: "tshirts",
    label: getProductTypeName(ProductType.TSHIRT),
    icon: Shirt,
  },
  {
    value: "weights",
    label: getProductTypeName(ProductType.WEIGHTS),
    icon: Dumbbell,
  },
  {
    value: "equipment",
    label: getProductTypeName(ProductType.EQUIPMENT),
    icon: Package,
  },
  {
    value: "accessories",
    label: getProductTypeName(ProductType.ACCESSORIES),
    icon: Watch,
  },
] as const;

type CategoryValue = (typeof categories)[number]["value"];

function StorePageComponent() {
  const { data } = useSuspenseQuery(listPublicProductsOptions());
  const search = Route.useSearch();
  const navigate = useNavigate();
  const { data: session } = authClient.useSession();

  const [activeCategory, setActiveCategory] = useState<CategoryValue>("all");
  const [sortBy, setSortBy] = useState<SortOption>("featured");
  const [showFeaturedOnly, setShowFeaturedOnly] = useState(false);

  const [checkoutUrl, setCheckoutUrl] = useState<string | null>(null);
  const [selectedProductName, setSelectedProductName] =
    useState<string>("Ürün Satın Alma");
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
  const [purchasingId, setPurchasingId] = useState<string | null>(null);

  // Handle payment status feedback from callback
  useEffect(() => {
    if (search.payment === "success") {
      toast.success(
        "Siparişiniz ve ödemeniz başarıyla alındı! Detaylar e-posta adresinize iletilecektir.",
        { duration: 6000 },
      );
      void navigate({ to: "/store", search: {}, replace: true });
    } else if (search.payment === "failed") {
      toast.error(
        search.error ||
          "Ödeme işlemi gerçekleştirilemedi. Lütfen tekrar deneyin.",
        { duration: 6000 },
      );
      void navigate({ to: "/store", search: {}, replace: true });
    }
  }, [search, navigate]);

  const checkoutMutation = useMutation({
    mutationFn: createProductPaymentSessionFn,
    onSuccess: (res) => {
      setPurchasingId(null);
      if (
        res.status === 200 &&
        res.body &&
        "paymentPageUrl" in res.body &&
        res.body.paymentPageUrl
      ) {
        setCheckoutUrl(res.body.paymentPageUrl);
        setIsCheckoutOpen(true);
      } else if (res.body && "error" in res.body) {
        toast.error(res.body.error);
      }
    },
    onError: (err) => {
      setPurchasingId(null);
      toast.error((err as Error).message || "Ödeme başlatılamadı");
    },
  });

  const handlePurchase = (productId: string, name: string) => {
    if (!session?.user) {
      toast.info("Satın alma işlemine devam etmek için lütfen giriş yapın.");
      void navigate({
        to: "/password/login",
        search: { redirect: "/store" },
      });
      return;
    }

    setSelectedProductName(name);
    setPurchasingId(productId);
    checkoutMutation.mutate({
      data: {
        productId,
      },
    });
  };

  const allProducts = (data.body?.all || []) as ProductItem[];
  const tshirts = (data.body?.tshirts || []) as ProductItem[];
  const weights = (data.body?.weights || []) as ProductItem[];
  const equipment = (data.body?.equipment || []) as ProductItem[];
  const accessories = (data.body?.accessories || []) as ProductItem[];

  // Get products based on category
  const getProductsByCategory = (category: CategoryValue): ProductItem[] => {
    switch (category) {
      case "tshirts":
        return tshirts;
      case "weights":
        return weights;
      case "equipment":
        return equipment;
      case "accessories":
        return accessories;
      default:
        return allProducts;
    }
  };

  // Apply filters and sorting
  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const filteredProducts = useMemo(() => {
    let products = getProductsByCategory(activeCategory);

    // Filter featured only
    if (showFeaturedOnly) {
      products = products.filter((p) => p.isFeatured);
    }

    // Sort products
    const sorted = [...products];
    switch (sortBy) {
      case "price-asc":
        sorted.sort((a, b) => a.price - b.price);
        break;
      case "price-desc":
        sorted.sort((a, b) => b.price - a.price);
        break;
      case "featured":
        sorted.sort((a, b) => {
          if (a.isFeatured && !b.isFeatured) return -1;
          if (!a.isFeatured && b.isFeatured) return 1;
          return a.displayOrder - b.displayOrder;
        });
        break;
      case "newest":
        sorted.sort((a, b) => b.displayOrder - a.displayOrder);
        break;
    }

    return sorted;
  }, [
    activeCategory,
    sortBy,
    showFeaturedOnly,
    allProducts,
    tshirts,
    weights,
    equipment,
    accessories,
  ]);

  const featuredCount = allProducts.filter((p) => p.isFeatured).length;

  return (
    <div className="min-h-screen w-full bg-background px-4 pt-28 pb-20 text-foreground antialiased sm:px-8">
      <div className="mx-auto w-full max-w-7xl">
        {/* Hero Section */}
        <div className="mb-12 text-center sm:mb-16">
          <div className="mx-auto max-w-3xl space-y-6">
            <h1 className="font-extrabold font-serif text-4xl tracking-tight sm:text-5xl lg:text-6xl">
              Mağaza
            </h1>
            <p className="mx-auto max-w-2xl text-lg text-muted-foreground leading-relaxed sm:text-xl">
              Spor yaşamınız için tasarlanmış kaliteli ürünler. T-shirtler,
              ağırlıklar, ekipmanlar ve daha fazlası.
            </p>
          </div>
        </div>

        {/* Category Tabs */}
        <div className="mb-8">
          <div className="flex flex-wrap justify-center gap-2">
            {categories.map((category) => {
              const Icon = category.icon;
              const isActive = activeCategory === category.value;
              return (
                <Button
                  key={category.value}
                  variant={isActive ? "default" : "outline"}
                  size="sm"
                  onClick={() => setActiveCategory(category.value)}
                  className={cn(
                    "gap-2 transition-all",
                    isActive && "shadow-md",
                  )}
                >
                  <Icon className="h-4 w-4" />
                  <span className="hidden sm:inline">{category.label}</span>
                  <span className="sm:hidden">
                    {category.value === "all" ? "Tümü" : ""}
                  </span>
                </Button>
              );
            })}
          </div>
        </div>

        {/* Filters Bar */}
        <div className="mb-8 flex flex-col gap-4 rounded-lg border bg-card p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium text-sm">Filtreler:</span>
            </div>

            {/* Featured Only Toggle */}
            <div className="flex items-center gap-2">
              <Switch
                id="featured-only"
                checked={showFeaturedOnly}
                onCheckedChange={setShowFeaturedOnly}
              />
              <label
                htmlFor="featured-only"
                className="flex cursor-pointer items-center gap-1 text-sm"
              >
                <Sparkles className="h-3 w-3 text-primary" />
                <span className="hidden sm:inline">Sadece Öne Çıkanlar</span>
                <span className="sm:hidden">Öne Çıkan</span>
                {featuredCount > 0 && (
                  <Badge variant="secondary" className="ml-1">
                    {featuredCount}
                  </Badge>
                )}
              </label>
            </div>
          </div>

          <div className="flex items-center gap-4">
            {/* Sort Dropdown */}
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground text-sm">Sırala:</span>
              <Select
                value={sortBy}
                onValueChange={(value) => setSortBy(value as SortOption)}
              >
                <SelectTrigger className="w-[160px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="featured">Öne Çıkanlar</SelectItem>
                  <SelectItem value="price-asc">
                    Fiyat: Düşükten Yükseğe
                  </SelectItem>
                  <SelectItem value="price-desc">
                    Fiyat: Yüksekten Düşüğe
                  </SelectItem>
                  <SelectItem value="newest">En Yeniler</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Results Count */}
            <Badge variant="outline" className="hidden sm:flex">
              {filteredProducts.length} ürün
            </Badge>
          </div>
        </div>

        {/* Active Filters */}
        {(showFeaturedOnly || activeCategory !== "all") && (
          <div className="mb-6 flex flex-wrap items-center gap-2">
            <span className="text-muted-foreground text-sm">
              Aktif Filtreler:
            </span>
            {activeCategory !== "all" && (
              <Badge variant="secondary" className="gap-1">
                {categories.find((c) => c.value === activeCategory)?.label}
                <button
                  onClick={() => setActiveCategory("all")}
                  className="ml-1 hover:text-destructive"
                >
                  ×
                </button>
              </Badge>
            )}
            {showFeaturedOnly && (
              <Badge variant="secondary" className="gap-1">
                Öne Çıkanlar
                <button
                  onClick={() => setShowFeaturedOnly(false)}
                  className="ml-1 hover:text-destructive"
                >
                  ×
                </button>
              </Badge>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setActiveCategory("all");
                setShowFeaturedOnly(false);
                setSortBy("featured");
              }}
              className="text-muted-foreground text-xs"
            >
              Tümünü Temizle
            </Button>
          </div>
        )}

        {/* Product Grid */}
        <ProductGrid
          products={filteredProducts}
          onPurchase={handlePurchase}
          purchasingId={purchasingId}
        />

        {/* Mobile Results Count */}
        <div className="mt-6 text-center sm:hidden">
          <Badge variant="outline">
            {filteredProducts.length} ürün gösteriliyor
          </Badge>
        </div>

        {/* Iyzico Modal Checkout */}
        <IyzicoCheckoutDialog
          open={isCheckoutOpen}
          onOpenChange={setIsCheckoutOpen}
          paymentPageUrl={checkoutUrl}
          title={`${selectedProductName} - Güvenli Ödeme`}
          description="İyzico güvencesiyle 256-bit SSL şifrelemeli 3D Secure ödeme."
        />

        {/* Sade Güvenli Ödeme & Sözleşmeler Bandı */}
        <div className="mt-12 flex flex-col items-center justify-between gap-4 rounded-xl border border-border/60 bg-muted/20 px-5 py-4 text-center sm:flex-row sm:text-left">
          <div className="space-y-1">
            <div className="flex items-center justify-center gap-1.5 font-medium text-foreground text-xs sm:justify-start">
              <ShieldCheck className="h-4 w-4 shrink-0 text-primary" />
              <span>İyzico 256-Bit SSL ile 3D Secure Güvenli Alışveriş</span>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-muted-foreground text-xs sm:justify-start">
              <Link
                to="/privacy"
                search={{ tab: "distance-sales" }}
                className="text-primary hover:underline"
              >
                Mesafeli Satış Sözleşmesi
              </Link>
              <span>•</span>
              <Link
                to="/privacy"
                search={{ tab: "delivery-return" }}
                className="text-primary hover:underline"
              >
                Teslimat & İade Politikası
              </Link>
            </div>
          </div>

          <div className="flex items-center justify-center rounded-lg border border-border/60 bg-white/95 px-3 py-1.5 dark:bg-card/80">
            <img
              src="/iyzico-logo-pack/footer_iyzico_ile_ode/Colored/logo_band_colored.svg"
              alt="İyzico ile Öde, Visa, Mastercard, Troy, Amex"
              className="h-5 w-auto object-contain"
              loading="lazy"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
